import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface Props {
  /** Etichetta del campo (sopra il controllo). */
  label: string;
  options: MultiSelectOption[];
  /** Valori selezionati; array vuoto = nessun filtro ("tutti"). */
  selected: string[];
  onChange: (next: string[]) => void;
  /** Testo del bottone quando non c'è selezione (es. "Tutti gli stati"). */
  allLabel: string;
}

/**
 * Filtro multi-selezione con checkbox. Nessuna dipendenza esterna: bottone +
 * pannello chiuso su click esterno / Escape. La selezione vuota equivale a
 * "nessun filtro" (il backend riceve il parametro assente).
 */
export default function MultiSelectFilter({ label, options, selected, onChange, allLabel }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggle = (value: string) => {
    onChange(
      selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value],
    );
  };

  const summary = (() => {
    if (selected.length === 0) return allLabel;
    const labels = options.filter((o) => selected.includes(o.value)).map((o) => o.label);
    if (labels.length <= 2) return labels.join(', ');
    return `${labels.length} selezionati`;
  })();

  return (
    <div ref={rootRef} className="relative">
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-left"
      >
        <span className={`truncate ${selected.length === 0 ? 'text-gray-500' : 'text-gray-900'}`}>
          {summary}
        </span>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto py-1">
          {options.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-400">Nessuna opzione</p>
          ) : (
            options.map((opt) => {
              const checked = selected.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(opt.value)}
                    className="sr-only"
                  />
                  <span
                    className={`flex-shrink-0 h-4 w-4 rounded border flex items-center justify-center ${
                      checked ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 bg-white'
                    }`}
                  >
                    {checked && <Check className="h-3 w-3" />}
                  </span>
                  <span className="truncate">{opt.label}</span>
                </label>
              );
            })
          )}
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full text-left px-3 py-1.5 text-xs text-blue-600 hover:bg-gray-50 border-t border-gray-100 mt-1"
            >
              Azzera selezione
            </button>
          )}
        </div>
      )}
    </div>
  );
}
